// Clickable KPI strip above the queue. Each card doubles as a one-click
// quick-filter (Open · Unassigned · SLA breached · Due today). Counts are
// derived from the portal-scoped ticket list (ignores the dropdown filters)
// so the numbers reflect the whole queue, JSM-style.
import React, { useMemo } from 'react';
import { Tray, UserMinus, WarningCircle, Clock } from '@phosphor-icons/react';

const OPEN_STATUSES = ['open', 'in_progress', 'pending'];

export const isOpen = (t) => OPEN_STATUSES.includes(t.status);
export const isUnassigned = (t) => isOpen(t) && !t.assignee_user_id;
export const isBreached = (t) => {
  const s = t.sla_state; if (!s) return false;
  return [s.response, s.resolution].filter(Boolean).some(c => c.breached || (c.completed && c.met === false));
};
export const isDueToday = (t) => {
  const s = t.sla_state; if (!s) return false;
  const now = new Date();
  const endOfDay = new Date(now); endOfDay.setHours(23, 59, 59, 999);
  const minsLeft = Math.round((endOfDay - now) / 60000);
  return [s.response, s.resolution].filter(Boolean).some(c => !c.completed && !c.breached && c.remaining_mins > 0 && c.remaining_mins <= minsLeft);
};

export const STAT_PREDICATES = { open: isOpen, unassigned: isUnassigned, breached: isBreached, due_today: isDueToday };

const CARDS = [
  { key: 'open', label: 'Open', icon: Tray, tone: 'text-blue-600', ring: 'ring-blue-200', activeBg: 'bg-blue-50 ring-blue-400' },
  { key: 'unassigned', label: 'Unassigned', icon: UserMinus, tone: 'text-zinc-600', ring: 'ring-zinc-200', activeBg: 'bg-zinc-100 ring-zinc-400' },
  { key: 'breached', label: 'SLA breached', icon: WarningCircle, tone: 'text-red-600', ring: 'ring-red-200', activeBg: 'bg-red-50 ring-red-400' },
  { key: 'due_today', label: 'Due today', icon: Clock, tone: 'text-amber-600', ring: 'ring-amber-200', activeBg: 'bg-amber-50 ring-amber-400' },
];

export default function QueueStats({ tickets, active, onPick }) {
  const counts = useMemo(() => ({
    open: tickets.filter(isOpen).length,
    unassigned: tickets.filter(isUnassigned).length,
    breached: tickets.filter(isBreached).length,
    due_today: tickets.filter(isDueToday).length,
  }), [tickets]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="sd-queue-stats">
      {CARDS.map(c => {
        const Icon = c.icon;
        const isActive = active === c.key;
        return (
          <button
            key={c.key}
            onClick={() => onPick(isActive ? null : c.key)}
            data-testid={`sd-stat-${c.key}`}
            className={`text-left rounded-xl bg-white ring-1 px-4 py-3 transition-all hover:shadow-sm hover:-translate-y-0.5 ${isActive ? c.activeBg + ' ring-2' : c.ring}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{c.label}</span>
              <Icon size={16} weight="fill" className={c.tone} />
            </div>
            <span className={`block mt-1.5 text-2xl font-bold ${counts[c.key] > 0 ? 'text-zinc-900' : 'text-zinc-300'}`}>{counts[c.key]}</span>
          </button>
        );
      })}
    </div>
  );
}
